<script lang="ts" setup>
import {computed, defineAsyncComponent, onMounted, ref} from 'vue'
import {
    ArrowsRightLeftIcon,
    CalendarDaysIcon,
    ChatBubbleLeftEllipsisIcon,
    ClockIcon,
    PhoneIcon,
    UserIcon
} from "@heroicons/vue/24/solid";
import SearchInput from "@src/components/ui/inputs/SearchInput.vue";
import ScheduleMessagesService from "@src/services/scheduleMessages";
const ModalScheduleEdit = defineAsyncComponent(() => import("@src/components/shared/modals/ScheduleMessageModal/ScheduleMessage.vue"));
const WaapiCampaign = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/WaapiCampaign.vue"));
import {useWAAPIStore} from "@src/stores/waapi";
import {ScheduledWaapiCampaign} from "@src/types/ScheduledWaapiCampaign";
import {Campaign} from "@src/types/Campaign";
import useStore from "@src/stores/store";
import NotifyToggle from "@src/components/ui/inputs/NotifyToggle.vue";
import SidebarHeader from "@src/components/views/HomeView/Sidebar/SidebarHeader.vue";
import InformationIcon from "@src/components/ui/utils/InformationIcon.vue";
import {permissionsHelper} from "@src/helpers/permissionsHelper";

const selectedMessage = ref<any>(null)

const scheduleMessages = ref<any[]>([])
const canScheduleMessage = ref(false)
const campaignForm = ref<ScheduledWaapiCampaign>()
const costumerToken = ref('')
const waapiStore = useWAAPIStore()
const store = useStore()
const no_token = ref(false);
const permissions = permissionsHelper.all()

const searchCustomer = ref('')
const searchUser = ref('')

const openModalScheduleEdit = ref<boolean>(false);
const scheduledCampaigns = ref<Campaign[]>([])

const loadMessages = async () => {
    const response = await ScheduleMessagesService.getAll()

    scheduleMessages.value = response.data
    scheduledCampaigns.value = response.campaigns
    canScheduleMessage.value = response.canScheduleMessage
}

const filteredMessages = computed(() => {
    return scheduleMessages.value.filter(message => {
        const customerQuery = searchCustomer.value.trim().toLowerCase()
        const userQuery = searchUser.value.trim().toLowerCase()

        const customerMatch = !customerQuery ||
            message.costumer_name?.toLowerCase().includes(customerQuery)

        const userMatch = !userQuery ||
            message.user_name?.toLowerCase().includes(userQuery)

        return customerMatch && userMatch
    })
})

const filteredScheduledCampaigns = computed(() => {
    return scheduledCampaigns.value?.filter((campaign: Campaign) => {
        const customerQuery = searchCustomer.value.trim().toLowerCase()
        const userQuery = searchUser.value.trim().toLowerCase()

        const customerMatch = !customerQuery ||
            campaign.costumer?.name?.toLowerCase().includes(customerQuery)

        const userMatch = !userQuery ||
            campaign.user_name?.toLowerCase().includes(userQuery)

        return customerMatch && userMatch
    })
})

const selectMessage = (message: any) => {
    selectedMessage.value = message

    no_token.value = false
    openModalScheduleEdit.value = true
}

const newMessage = () => {
    selectedMessage.value = {
        id: -1,
        channel_id: null,
        transferred_operator_id: null,
        scheduled_at: null,
        message: '',
    }

    no_token.value = true
    openModalScheduleEdit.value = true
}

onMounted(async () => {
    await loadMessages()
})

const handleSaved = async () => {
    openModalScheduleEdit.value = false

    const response = await ScheduleMessagesService.getAll()

    scheduleMessages.value = response.data
    scheduledCampaigns.value = response.campaigns
}

const setCampaignForm = (form: ScheduledWaapiCampaign) => {
    campaignForm.value = form
    costumerToken.value = form.token
    waapiStore.showCampaignModal = true
}
</script>


<template>
    <div ref="container"
         class="grow px-5 flex flex-col overflow-y-scroll scrollbar-hidden">

        <SidebarHeader>
            <!--title-->
            <template v-slot:title>Mensagens Agendadas</template>
            <template v-slot:actions>
                <div class="flex items-center">
                    <ClockIcon class="w-[24px] h-[24px] font-semibold text-gray-500"/>
                    <InformationIcon title="Mensagens Agendadas" text="Consulte e gerencie mensagens programadas para envio futuro."></InformationIcon>
                </div>
            </template>
        </SidebarHeader>

        <!-- filtros -->
        <div class="flex flex-col items-stretch mb-4 mx-4 mt-6 md:flex-row md:items-center">
            <div class="flex items-center w-full md:w-auto">
                <UserIcon class="w-6 h-6 mr-3 text-brand-400 dark:text-brand-400 shrink-0"/>
                <SearchInput
                    class="flex-1"
                    :value="searchUser"
                    :full-width="true"
                    placeholder="Buscar por operador"
                    @value-changed="value => searchUser = value"
                />
            </div>
            <div class="flex items-center w-full md:w-auto md:ml-4 pt-3 md:pt-0">
                <PhoneIcon class="w-6 h-6 mr-3 text-brand-400 dark:text-brand-400 shrink-0 md:ml-5"/>
                <SearchInput
                    class="flex-1"
                    :value="searchCustomer"
                    :full-width="true"
                    placeholder="Buscar por cliente"
                    @value-changed="value => searchCustomer = value"
                />
            </div>

            <button v-if="canScheduleMessage"
                    class="flex mt-4 w-full justify-center rounded-md py-3 px-4 bg-green-500 text-white font-semibold cursor-pointer hover:bg-green-600 md:mt-0 md:ml-5 md:w-[245px]"
                    @click="newMessage()">
                Agendar mensagem
            </button>
        </div>

        <div
            class="grid grid-cols-1 gap-5 px-4 max-h-[65vh] overflow-y-auto md:grid-cols-2 2xl:grid-cols-3 pb-5 view-scroll pt-4 md:max-h-[80vh]">

            <div
                v-for="message in filteredMessages"
                :key="message.id"
                class="w-full pb-3"
            >
                <div
                    class="filtered-messages flex flex-col md:flex-row w-full justify-between border-2 rounded p-4 shadow-md
                   transition-all duration-300 hover:-translate-y-1 hover:shadow-lg min-h-55 dark:bg-gray-700 dark:border-none">
                    <div class="w-full flex flex-col justify-between">
                        <div class="w-full">
                            <div class="w-full flex justify-between mt-2">
                                <div class="flex items-center">
                                    <UserIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                    <h4 class="heading-2 text-black/70 dark:text-white/70">
                                        <span class="text-brand-500 dark:text-brand-400">Atendente: </span>
                                        {{ message.user_name }}
                                    </h4>
                                </div>

                                <div class="flex items-center">
                                    <CalendarDaysIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                    <h4 class="heading-2 text-black/70 dark:text-white/70">
                                        {{ new Date(message.scheduled_at).toLocaleString('pt-BR') }}
                                    </h4>
                                </div>
                            </div>

                            <div class="flex items-center mt-3">
                                <PhoneIcon class="w-5 h-5 mr-3 text-orange-500 dark:text-orange-400"/>
                                <h4 class="heading-2 text-black/70 dark:text-white/70">
                                    <span class="text-orange-500 dark:text-orange-400">Cliente: </span>
                                    {{ message.costumer_token }}
                                </h4>
                            </div>

                            <div class="flex items-start mt-4">
                                <ChatBubbleLeftEllipsisIcon
                                    class="min-w-5 h-5 mr-3 text-green-500 dark:text-green-500"/>
                                <h4 class="heading-2 text-black/70 font-normal text-start bg-green-100 p-3 px-4 rounded-sm dark:text-[#FAFAFA] dark:bg-[#144D37] line-clamp-5 overflow-y-auto message-scroll">
                                    {{ message.message }}
                                </h4>
                            </div>
                        </div>
                        <div class="flex items-center mt-3">
                            <ArrowsRightLeftIcon class="min-w-5 h-5 mr-3 text-red-600 dark:text-red-400"/>
                            <h4 class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                Após o envio,
                                <span v-if="!message.transferred_operator_name"> não transfere o atendimento</span>
                                <span v-if="message.transferred_operator_name"> transferir para </span>
                                <span class="font-bold ml-1">{{ message.transferred_operator_name }}</span>
                            </h4>
                        </div>
                    </div>

                    <div class="mt-2 ml-4">
                        <Button v-if="canScheduleMessage"
                                class="w-full rounded-md py-3 px-4 bg-brand-400 text-gray-100 font-semibold cursor-pointer hover:bg-brand-500"
                                @click="selectMessage(message)"
                        >
                            Editar
                        </Button>
                    </div>
                </div>
            </div>
            <div
                v-for="campaign in filteredScheduledCampaigns"
                :key="campaign.id"
                class="w-full pb-3"
            >
                <div
                    class="filtered-scheduled-campaigns flex flex-col md:flex-row w-full justify-between border-2 rounded p-4 shadow-md
                   transition-all duration-300 hover:-translate-y-1 hover:shadow-lg h-55 dark:bg-gray-700 dark:border-none">
                    <div class="w-full flex flex-col justify-between">
                        <div class="w-full">
                            <div class="w-full flex justify-between mt-2">
                                <div class="flex items-center">
                                    <UserIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                    <h4 class="heading-2 text-black/70 dark:text-white/70">
                                        <span class="text-brand-500 dark:text-brand-400">Atendente: </span>
                                        {{ campaign.user_name }}
                                    </h4>
                                </div>

                                <div class="flex items-center">
                                    <CalendarDaysIcon class="w-5 h-5 mr-3 text-brand-500 dark:text-brand-400"/>
                                    <h4 class="heading-2 text-black/70 dark:text-white/70">
                                        {{
                                            new Date(campaign.scheduled_at).toLocaleString('pt-BR')
                                        }}
                                    </h4>
                                    <div class="mt-2 ml-4">
                                        <Button v-if="canScheduleMessage && permissions.campaign_edit"
                                                class="flex justify-center rounded-md py-3 px-4 bg-brand-400 text-gray-100 font-semibold cursor-pointer hover:bg-brand-500"
                                                @click="selectMessage({
                                                    id: campaign.id,
                                                    channel_id: campaign.channel_id,
                                                    transferred_operator_id: campaign.transferred_operator_id,
                                                    scheduled_at: campaign.scheduled_at,
                                                    message: campaign.model?.name,
                                                })"
                                        >
                                            Editar
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div class="flex items-center mt-3">
                                <PhoneIcon class="w-5 h-5 mr-3 text-orange-500 dark:text-orange-400"/>
                                <h4 class="heading-2 text-black/70 dark:text-white/70">
                                    <span class="text-orange-500 dark:text-orange-400">Cliente: </span>
                                    {{ campaign.costumer?.token }}
                                </h4>
                            </div>

                            <div class="flex items-start mt-4">
                                <img v-if="!store.settings.darkMode"
                                     class="w-6 h-6 mr-3"
                                     src="@src/assets/vectors/official_icon.svg"/>
                                <img v-else class="w-6 h-6 mr-3"
                                     src="@src/assets/vectors/official_icon_white.svg"/>
                                <h4 class="heading-2 text-black/70 font-normal text-start bg-black/10 p-3 px-4 rounded-sm dark:text-[#FAFAFA] dark:bg-black/20 line-clamp-5 overflow-y-auto message-scroll">
                                    {{ campaign.model?.name || 'Modelo excluido' }}
                                </h4>
                            </div>
                        </div>
                        <div class="flex items-center mt-3">
                            <ArrowsRightLeftIcon class="min-w-5 h-5 mr-3 text-red-600 dark:text-red-400"/>
                            <h4 class="heading-2 text-black/70 dark:text-white/70 font-normal italic">
                                Após o envio,
                                <span v-if="!campaign.transferred_operator_name"> não transfere o atendimento</span>
                                <span v-if="campaign.transferred_operator_name"> transferir para </span>
                                <span class="font-bold ml-1">{{ campaign.transferred_operator_name }}</span>
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            <Teleport to="#modal-root">
                <Transition
                    v-if="openModalScheduleEdit"
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 translate-y-2"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-2">
                    <ModalScheduleEdit
                        :costumer_name="selectedMessage?.costumer_name ?? ''"
                        :costumer_token="null"
                        :message="selectedMessage"
                        :no_token="no_token"
                        :session_id="null"
                        @closeModal="openModalScheduleEdit = false"
                        @saved="handleSaved()"
                        @scheduleCampaignForm="setCampaignForm"
                    />
                </Transition>
                <Transition>
                    <WaapiCampaign v-if="waapiStore.showCampaignModal" :costumer-token="costumerToken"
                                   :form="campaignForm" @reload="loadMessages"/>
                </Transition>
            </teleport>
        </div>
    </div>
</template>

<style scoped>
.schedule-item {
    transition: all 0.3s ease;
}

.schedule-item:hover {
    transform: translateX(-5px);
}

.schedule-item:hover > div {
    transform: scale(1.05);
}

.schedule-item:hover > button {
    transform: scale(1.1);
}

.schedule-item > div,
.schedule-item > button {
    transition: transform 0.2s ease;
}

/* Chrome, Edge, Safari */
.view-scroll::-webkit-scrollbar {
    height: 30px;
}

.view-scroll::-webkit-scrollbar-track {
    background: rgba(86, 86, 86, 0.15);
    border-radius: 8px;
}

.view-scroll::-webkit-scrollbar-thumb {
    background: rgba(86, 86, 86, 0.45);
    border-radius: 8px;
}

/* hover = mesma cor do original */
.view-scroll::-webkit-scrollbar-thumb:hover,
.view-scroll::-webkit-scrollbar-thumb:active {
    background: rgba(86, 86, 86, 0.45);
}


/* Firefox */
.view-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(124, 140, 245, 0.90) rgba(86, 86, 86, 0.15);
}


/* Chrome, Edge, Safari */
.message-scroll::-webkit-scrollbar {
    height: 30px;
}

.message-scroll::-webkit-scrollbar-track {
    background: rgba(86, 86, 86, 0.15);
    border-radius: 8px;
}

.message-scroll::-webkit-scrollbar-thumb {
    background: rgba(86, 86, 86, 0.45);
    border-radius: 8px;
}

/* hover = mesma cor do original */
.message-scroll::-webkit-scrollbar-thumb:hover,
.message-scroll::-webkit-scrollbar-thumb:active {
    background: rgba(86, 86, 86, 0.45);
}


/* Firefox */
.message-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(53, 158, 53, 0.65) rgba(86, 86, 86, 0.15);
}


</style>